import { Button } from "@heroui/react";
import { ArrowRight, RefreshCw } from "lucide-react";
import { useEffect } from "react";
import { Link } from "react-router";
import { MediaImage } from "../../../components/MediaImage";
import type { HomeStoryShowcase } from "../../../hooks/home-story-showcase";
import { storyScrollDistance, useHomeStoryScene, type HomeStoryStatus } from "../../../hooks/useHomeStoryScene";
import { HOME_HERO_SCENE } from "../home-data";
import { chapterBody, homeStoryChapters, type HomeStoryChapter } from "../home-story-data";
import { HomeSkip } from "./HomeSkip";
import { HomeStoryOpening } from "./HomeStoryOpening";
import { HomeStoryRail } from "./HomeStoryRail";

type HomeStoryProps = {
  signedIn: boolean;
  loadingSession: boolean;
  showcase: HomeStoryShowcase | null;
  onStatusChange: (status: HomeStoryStatus) => void;
  nextSection: string;
};

export function HomeStory({ signedIn, loadingSession, showcase, onStatusChange, nextSection }: HomeStoryProps) {
  const story = useHomeStoryScene(showcase);

  useEffect(() => {
    onStatusChange(story.status);
  }, [onStatusChange, story.status]);

  const jumpTo = (chapter: HomeStoryChapter) => {
    const section = story.sectionRef.current;
    if (!section) return;
    const top = section.getBoundingClientRect().top + window.scrollY;
    const distance = storyScrollDistance(section.offsetHeight, window.innerHeight);
    window.scrollTo({ top: top + distance * chapter.stop, behavior: "smooth" });
  };

  return (
    <section
      ref={story.sectionRef}
      className={`home-story is-chapter-${story.chapter} is-${story.status}`}
      aria-labelledby="home-story-title"
    >
      <div className="home-story-stage">
        {story.status === "error" ? (
          <div className="home-story-fallback">
            <MediaImage src={HOME_HERO_SCENE} alt="绿色布艺单椅与水磨石、胡桃木、玻璃样板组成的材质场景" eager />
          </div>
        ) : null}
        <div
          ref={story.stageRef}
          className="home-story-canvas"
          role="img"
          aria-label="三维模型随滚动演示创作流程：白模、材质、工艺、参考素材，最后合成一张情绪板"
        />

        <HomeStoryOpening signedIn={signedIn} loadingSession={loadingSession} />

        {homeStoryChapters.map((chapter, index) => (
          <article
            key={chapter.id}
            className={`home-story-caption is-${chapter.id}${story.chapter === index + 1 ? " is-active" : ""}`}
          >
            <p className="home-story-step" aria-label={`第 ${index + 1} 步，共 ${homeStoryChapters.length} 步`}>
              <b>{chapter.number.padStart(2, "0")}</b>
              <i aria-hidden="true">
                <em style={{ transform: `scaleX(${(index + 1) / homeStoryChapters.length})` }} />
              </i>
              <span aria-hidden="true">{String(homeStoryChapters.length).padStart(2, "0")}</span>
            </p>
            <h2>{chapter.title}</h2>
            <p className="home-story-body">{chapterBody(chapter, showcase)}</p>
            {chapter.id === "board" ? (
              <div className="home-story-actions">
                <Link
                  className="home-cta is-solid"
                  to={signedIn ? "/workbench" : "/moodboard"}
                  tabIndex={story.chapter === homeStoryChapters.length ? 0 : -1}
                >
                  <span>{signedIn ? "打开工作台" : "搭建我的情绪板"}</span>
                  <i aria-hidden="true">
                    <ArrowRight size={18} strokeWidth={1.8} />
                  </i>
                </Link>
                <a
                  className="home-cta is-text"
                  href={`#${nextSection}`}
                  tabIndex={story.chapter === homeStoryChapters.length ? 0 : -1}
                >
                  {nextSection === "home-characters" ? "认识材质与应用" : "看社区精选"}
                </a>
              </div>
            ) : null}
          </article>
        ))}

        <div className="home-story-frame" aria-hidden="true">
          <i />
          <i />
          <i />
          <i />
        </div>

        <HomeStoryRail chapters={homeStoryChapters} active={story.chapter} onSelect={jumpTo} />

        <HomeSkip
          targetId={nextSection}
          label="跳过演示"
          className="is-story"
          visible={story.chapter > 0 && story.chapter < homeStoryChapters.length}
        />

        <span className="home-story-cue" aria-hidden="true">
          向下滚动
          <i />
        </span>

        {story.assetWarning && (
          <div className="home-story-status" role="status">
            {story.assetWarning}
            <Button size="sm" variant="tertiary" onPress={story.retry}>
              重新加载
            </Button>
          </div>
        )}
        {story.status === "loading" ? (
          <span className="home-story-status" role="status">
            正在载入 3D 场景
          </span>
        ) : null}
        {story.status === "error" ? (
          <div className="home-story-status is-error" role="status">
            3D 场景无法在此设备上运行，已换成静态图。
            <Button size="sm" variant="tertiary" onPress={story.retry}>
              <RefreshCw size={14} />
              重试
            </Button>
          </div>
        ) : null}
      </div>
    </section>
  );
}
